/*
 Copyright 2008 Cheezeworld.com

 Permission is hereby granted, free of charge, to any person
 obtaining a copy of this software and associated documentation
 files (the "Software"), to deal in the Software without
 restriction, including without limitation the rights to use,
 copy, modify, merge, publish, distribute, sublicense, and/or sell
 copies of the Software, and to permit persons to whom the
 Software is furnished to do so, subject to the following
 conditions:

 The above copyright notice and this permission notice shall be
 included in all copies or substantial portions of the Software.

 THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
 EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES
 OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
 NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
 WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR
 OTHER DEALINGS IN THE SOFTWARE.
 */

import { warn } from "../logging/warn";

import Point = createjs.Point;

export class Vector2D {

    public get length(): number {
        //length = sqrt(x^2 + y^2)

        // ..Only calculate length if it has changed ...
        if (this._oldX != this.x || this._oldY != this.y) {
            this._oldX = this.x;
            this._oldY = this.y;
            this._length = Math.sqrt((this.x * this.x) + (this.y * this.y));
        }

        return this._length;
    }

    public get lengthSq(): number {
        return this.x * this.x + this.y * this.y;
    }

    public isZero(): boolean {
        return (this.x == 0 || this.y == 0);
    }

    public x: number;
    public y: number;

    //Constructor -------------------------------------------------
    constructor(X: number = 0, Y: number = 0) {
        this.x = X;
        this.y = Y;

        //to avoid slowdowns when creating new Vectors, length
        //is calculated when requested rather than on creation
        this._length = 0;
    }

    //--------------------------------------------------------------

    // -- STATIC --

    /**
     * Will determine if someone at pos1st who is facing facing1st
     * that has a FOV of fov can see an object at pos2nd
     * @param pos1st The position of the "thing" looking
     * @param facing1st The unit length heading vector of which the "thing" is facing.
     * @param fov How wide the FOV of the "thing" is.
     * @param pos2nd The position of the object the "thing" is looking at.
     * @return returns true if the "thing" can see the object.
     *
     */
    public static is2ndInFOVof1st(pos1st: Vector2D, facing1st: Vector2D, fov: number, pos2nd: Vector2D): boolean {
        let toTarget: Vector2D = pos2nd.copy();
        toTarget.subtract(pos1st);
        toTarget.normalize();
        return facing1st.dotOf(toTarget) >= Math.cos(fov / 2.0);
    }

    /**
     * Converts a Point object to a Vector
     * @param point the Point object to convert
     * @return the newly constructed Vector
     *
     */
    public static pointToVector(point: Point): Vector2D {
        return new Vector2D(point.x, point.y);
    }

    /**
     * This will convert a rotation to a heading vector
     * @param rotInDegrees the rotation value to be converted (in degrees)
     * @return Returns a unit length vector of <code>rotInDegrees</code> heading
     *
     */
    public static rotToHeading(a_rotInRadians: number): Vector2D {
        let xPart: number = Math.cos(a_rotInRadians);
        let yPart: number = Math.sin(a_rotInRadians);
        return new Vector2D(xPart, yPart);
    }

    // -- CONVERSIONS / UTILITIES --

    public toString(): string {
        return ("( " + this.x + ", " + this.y + " )");
    }

    /**
     * Converts this Vector to a Point Object
     * @return A Point Object
     *
     */
    public toPoint(): Point {
        return new Point(this.x, this.y);
    }

    /**
     * Used to determine what angle this vector is from 0,0
     * @return The angle in radians
     *
     */
    public toRotation(): number {
        //calc the angle
        let ang: number = Math.atan(this.y / this.x);

        //if it is in the first quadrant
        if (this.y < 0 && this.x > 0) {
            return ang;
        }

        //if its in the 2nd or 3rd quadrant
        if ((this.y < 0 && this.x < 0) || (this.y > 0 && this.x < 0)) {
            return ang + 3.141592653589793;
        }
        //it must be in the 4th quadrant so:
        return ang + 6.283185307179586;
    }

    /**
     * An easy way to "set" the x/y value of this Vector
     * @param x
     * @param y
     *
     */
    public Set(x: number, y: number): void {
        this.x = x;
        this.y = y;
    }

    /**
     * Easy way to make a copy of this Vector
     * @return a New Vector object with the same properties as this Vector
     *
     */
    public copy(): Vector2D {
        let newVector: Vector2D = new Vector2D(this.x, this.y);
        newVector._length = this._length;
        newVector._oldX = this.x;
        newVector._oldY = this.y;
        return newVector;
    }

    // -- MODIFY FUNCTIONS -- These functions will perform modifications on THIS vector --

    /**
     * Shortens this Vector down to unit length.
     *
     */
    public normalize(): void {
        if (this.length != 0) {
            this.x /= this._length;
            this.y /= this._length;
        }
    }

    /**
     * Will reflect this vector upon the supplied vector.
     * (like the path of a ball bouncing off a wall)
     * @param norm The unit length "normal" vector to reflect upon.
     *
     */
    public reflect(norm: Vector2D): void {
        //this += 2.0 * this.dot(norm) * norm.getReverse();
        this.v1 = norm.getReverse();
        this.v1.multiply(2.0 * this.dotOf(norm));
        this.addTo(this.v1);
    }

    /**
     * Will add two Vectors together
     * @param vector The Vector to add to this one.
     *
     */
    public addTo(vector: Vector2D): void {
        this.x += vector.x;
        this.y += vector.y;
    }

    /**
     * Will subtract two Vectors together
     * @param vector The Vector to subtract from this one.
     *
     */
    public subtract(vector: Vector2D): void {
        this.x -= vector.x;
        this.y -= vector.y;
    }

    /**
     * Will multiply by a scalar number
     * @param scalar The Number to multiply by.
     *
     */
    public multiply(scalar: number): void {
        this.x *= scalar;
        this.y *= scalar;
    }

    /**
     * Will Divide by a scalar number
     * @param scalar The Number to divide by.
     *
     */
    public divide(scalar: number): void {
        if (scalar == 0) {
            warn("Illegal Divide by Zero!");
        }
        else {
            this.x /= scalar;
            this.y /= scalar;
        }
    }

    /**
     * Will keep this vector no longer than the max value supplied
     * @param max The max length of this vector.
     *
     */
    public truncate(max: number): void {
        if (this.lengthSq > max * max) {
            this.normalize();
            this.multiply(max);
        }
    }

    /**
     * Will keep this Vector inside a region by wrapping it around the sides
     * (This is considering the vector is being used as a position)
     * @param topLeft The top left point of the 'region' to wrap around
     * @param bottomRight The bottom right point of the 'region' to wrap around
     *
     */
    public wrapAround(topLeft: Vector2D, botRight: Vector2D): void {
        if (this.x > botRight.x) {
            this.x = topLeft.x + (this.x - botRight.x);
        }
        else if (this.x < topLeft.x) {
            this.x = botRight.x + this.x;
        }

        if (this.y < topLeft.y) {
            this.y = botRight.y + this.y;
        }
        else if (this.y > botRight.y) {
            this.y = topLeft.y + (this.y - botRight.y);
        }
    }

    // -- RETURN / GETTER FUNCTIONS -- These functions return a calculation but do not modify this Vector

    /**
     * Will return the result of this Vector added to another.
     * @param vector Vector to add to.
     * @return The result of the addition.
     *
     */
    public addedTo(vector: Vector2D): Vector2D {
        return new Vector2D(this.x + vector.x, this.y + vector.y);
    }

    /**
     * Will return the result of this Vector subtracted by another.
     * @param vector Vector to subtract by
     * @return  The result of the subtraction.
     *
     */
    public subtractedBy(vector: Vector2D): Vector2D {
        return new Vector2D(this.x - vector.x, this.y - vector.y);
    }

    /**
     * Will return the result of this Vector multiplied by another.
     * @param scalar Number to multiply by
     * @return  The result of the multiplication.
     *
     */
    public multipliedBy(scalar: number): Vector2D {
        return new Vector2D(this.x * scalar, this.y * scalar);
    }

    /**
     * Will return the result of this Vector divided by another.
     * @param scalar Number to divide by
     * @return  The result of the division.
     *
     */
    public dividedBy(scalar: number): Vector2D {
        if (scalar == 0) {
            warn("Illegal Divide by Zero!");
            return new Vector2D();
        }
        else {
            return new Vector2D(this.x / scalar, this.y / scalar);
        }
    }

    /**
     * Will give the normalized version of this Vector.
     * @return The normalized version of this Vector.
     *
     */
    public getNormalized(): Vector2D {
        if (this.length == 0) {
            return new Vector2D();
        }
        return new Vector2D(this.x / this._length, this.y / this._length);
    }

    /**
     * Will give the reverse of this Vector.
     * @return The reversed version of this Vector.
     *
     */
    public getReverse(): Vector2D {
        return new Vector2D(-this.x, -this.y);
    }

    /**
     * Will give the sign of this vector in relation to another.
     * (Useful for certain calculations)
     * @param vector The Vector to test again.
     * @return -1 if vector is counterclockwise, else 1 if clockwise.
     *
     */
    public sign(vector: Vector2D): number {
        if (this.y * vector.x > this.x * vector.y) {
            return -1;
        }
        else {
            return 1;
        }
    }

    /**
     * Determines if this Vector is parallel to another.
     * @param vector The Vector to test again.
     * @return true if these Vectors are parallel
     *
     */
    public isParallelTo(vector: Vector2D): boolean {
        this.v1 = this.copy();
        this.v1.normalize();
        this.v2 = vector.copy();
        this.v2.normalize();
        return ((this.v1.x == this.v2.x && this.v1.y == this.v2.y) || (this.v1.x == -this.v2.x && this.v1.y == -this.v2.y));
    }

    /**
     * Determines the perpendicular Vector to this.
     * (otherwise known as the right hand normal)
     * @return A perpendicular Vector to this one.
     *
     */
    public getPerp(): Vector2D {
        return new Vector2D(-this.y, this.x);
    }

    /**
     * Calculates the dot to another Vector.
     * |V1| . |V2|
     * @param vector The Vector to calculate with.
     * @return The resulting dot product.
     *
     */
    public dotOf(vector: Vector2D): number {
        return (this.x * vector.x) + (this.y * vector.y);
    }

    /**
     * Determines the cross product of this Vector and another.
     * This is the dame as the dot product of this and the
     * lefthand normal of the other.
     * @param vector Vector to calculate against.
     * @return The resulting cross product.
     *
     */
    public crossOf(vector: Vector2D): number {
        return (this.x * vector.y) - (this.y * vector.x);
    }

    /**
     * Use to determine the angle between this and another Vector.
     * @param vector The Vector to test against.
     * @return The resulting angle between these two Vectors in radians
     *
     */
    public angleTo(vector: Vector2D): number {
        return Math.acos(this.dotOf(vector) / (this.length * vector.length));
    }

    /**
     * Use to find the dot prod of another Vector's perpendicular Vector.
     * @param vector The Vector to test against.
     * @return The resulting dot product.
     *
     */
    public perpDotOf(vector: Vector2D): number {
        return this.getPerp().dotOf(vector);
    }

    /**
     * Use to find the projection of this onto another Vector.
     * @param vector The vector to project upon
     * @return The resulting projected Vector.
     *
     */
    public projectionOn(vector: Vector2D): Vector2D {
        this.v1 = vector.copy();
        this.v1.multiply(this.dotOf(vector) / vector.dotOf(vector));
        return this.v1;
    }

    /**
     * Use to find the distance to another Vector.
     * @param vector The Vector to test distance with.
     * @return The distance between these two Vectors.
     *
     */
    public distanceTo(vector: Vector2D): number {
        let xSep: number = vector.x - this.x;
        let ySep: number = vector.y - this.y;
        return Math.sqrt(ySep * ySep + xSep * xSep);
    }

    /**
     * Same as <code>distanceTo</code> but avoids the square root.
     * Use this any time the exact distance isn't exactly required.
     * @param vector The Vector to test distance with.
     * @return The resulting distance * distance
     *
     */
    public distanceSqTo(vector: Vector2D): number {
        let xSep: number = vector.y - this.y;
        let ySep: number = vector.x - this.x;
        return ySep * ySep + xSep * xSep;
    }

    /**
     * Use to determine if this Vector is within a bounds
     * (considering it is actually a point)
     * @param topLeft top left 'point' of the region.
     * @param botRight bot right 'point' of the region.
     * @return
     *
     */
    public isInsideRegion(topLeft: Vector2D, botRight: Vector2D): boolean {
        return !((this.x < topLeft.x) || (this.x > topLeft.x + botRight.x) || (this.y < topLeft.y) || (this.y > topLeft.y + botRight.y));
    }

    // -- PRIVATE --

    private _length: number;

    //temporary vars so there is no mass object creation
    private v1: Vector2D;
    private v2: Vector2D;
    //used to save time on length calculation
    private _oldX: number;
    private _oldY: number;
}
